import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Autocomplete,
} from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import Tooltip from '@mui/material/Tooltip';
import GlobalIpRules from './GlobalIpRules';
import VersionsTab from './VersionsTab';
import HistoryTab from './HistoryTab';
import DashboardTab from './DashboardTab';
import { getConfig } from './config';
import ReCAPTCHA from 'react-google-recaptcha';


function getLocalIP(callback) {
  const pc = new window.RTCPeerConnection({iceServers: []});
  pc.createDataChannel('');
  pc.createOffer().then(offer => pc.setLocalDescription(offer));
  pc.onicecandidate = (event) => {
    if (!event || !event.candidate) {
      pc.close();
      callback("");
      return;
    }
    const ipMatch = /([0-9]{1,3}(\.[0-9]{1,3}){3})/.exec(event.candidate.candidate);
    if (ipMatch) {
      callback(ipMatch[1]);
      pc.close();
    }
  };
}

function isValidIPv4(ip) {
  return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [licenses, setLicenses] = useState([]); // always an array
  const [form, setForm] = useState({ appType: "Arkhon", permissions: "{}" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");

  // Add License form state
  const [addForm, setAddForm] = useState({
    appType: "",
    clientName: "",
    permissions: {},
  });
  const [addError, setAddError] = useState("");
  const [expirationDate, setExpirationDate] = useState(new Date());

  // Edit License form state
  const [editLicense, setEditLicense] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editExpirationDate, setEditExpirationDate] = useState(new Date());
  const [editError, setEditError] = useState("");

  // Initialize state from URL query params
  const _initParams = new URLSearchParams(window.location.search);

  const [sortColumn, setSortColumn] = useState(_initParams.get("sortColumn") || "expirationDate");
  const [sortDirection, setSortDirection] = useState(_initParams.get("sortDirection") || "asc");

  // Licenses pagination & filter state
  const [licensePage, setLicensePage] = useState(0);
  const [licenseRowsPerPage, setLicenseRowsPerPage] = useState(10);
  const [licenseTotal, setLicenseTotal] = useState(0);
  const [licenseFilters, setLicenseFilters] = useState({
    licenseId: _initParams.get("licenseId") || '',
    clientName: _initParams.get("clientName") || '',
    applicationName: _initParams.get("applicationName") || '',
    expirationStart: _initParams.get("expirationStart") ? new Date(_initParams.get("expirationStart")) : null,
    expirationEnd: _initParams.get("expirationEnd") ? new Date(_initParams.get("expirationEnd")) : null,
  });

  // Add this variable at the top level of the App component, before the return statement
  const [userRole, setUserRole] = useState(localStorage.getItem("admin_role") || "admin");
  const [tab, setTab] = useState(_initParams.get("tab") || "dashboard");
  // Applications tab state
  const [applications, setApplications] = useState([]);
  const [appLoading, setAppLoading] = useState(false);
  const [appError, setAppError] = useState("");
  const [newAppName, setNewAppName] = useState("");
  const [permissions, setPermissions] = useState({}); // { appId: [permission, ...] }
  const [permLoading, setPermLoading] = useState({}); // { appId: bool }
  const [permError, setPermError] = useState({}); // { appId: string }
  const [newPermName, setNewPermName] = useState({}); // { appId: string }

  // Add state for licenses tab
  const [licenseAppId, setLicenseAppId] = useState("");
  const [licensePerms, setLicensePerms] = useState({}); // { permId: bool }
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [licenseDialogMode, setLicenseDialogMode] = useState("add"); // 'add' or 'edit'
  const [licenseForm, setLicenseForm] = useState({ id: null, clientName: "", expirationDate: "", allowExpiredValidation: false });
  const [licenseError, setLicenseError] = useState("");
  const [licenseLoading, setLicenseLoading] = useState(false);

  // Sorting handler
  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  const [apiBase, setApiBase] = useState(null);
  const [configError, setConfigError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
  const [latestVersion, setLatestVersion] = useState('');
  const [dashAlerts, setDashAlerts] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loginUsername, setLoginUsername] = useState(localStorage.getItem("admin_username") || '');

  // Move all useEffect calls to the top level, before any return or conditional
  useEffect(() => {
    getConfig().then(cfg => {
      setApiBase(cfg.API_BASE);
      setRecaptchaSiteKey(cfg.RECAPTCHA_SITE_KEY);
    }).catch(e => setConfigError(e.message));
  }, []);

  // Sync licenses tab filters + tab to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("tab", tab);
    if (tab === "licenses") {
      if (licenseFilters.licenseId) params.set("licenseId", licenseFilters.licenseId); else params.delete("licenseId");
      if (licenseFilters.clientName) params.set("clientName", licenseFilters.clientName); else params.delete("clientName");
      if (licenseFilters.applicationName) params.set("applicationName", licenseFilters.applicationName); else params.delete("applicationName");
      if (licenseFilters.expirationStart) params.set("expirationStart", licenseFilters.expirationStart.toISOString()); else params.delete("expirationStart");
      if (licenseFilters.expirationEnd) params.set("expirationEnd", licenseFilters.expirationEnd.toISOString()); else params.delete("expirationEnd");
      if (sortColumn !== "expirationDate") params.set("sortColumn", sortColumn); else params.delete("sortColumn");
      if (sortDirection !== "asc") params.set("sortDirection", sortDirection); else params.delete("sortDirection");
    } else {
      ["licenseId","clientName","applicationName","expirationStart","expirationEnd","sortColumn","sortDirection"].forEach(k => params.delete(k));
    }
    window.history.replaceState(null, "", `?${params.toString()}`);
    // eslint-disable-next-line
  }, [tab, licenseFilters, sortColumn, sortDirection]);

  useEffect(() => {
    if (!apiBase) return;
    if (token) fetchLicenses();
    // eslint-disable-next-line
  }, [token, apiBase, licensePage, licenseRowsPerPage, sortColumn, sortDirection]);

  useEffect(() => {
    if (!apiBase) return;
    if (tab === "applications" && token) {
      setAppLoading(true);
      setAppError("");
      fetch(`${apiBase}/applications`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(apps => {
          setApplications(Array.isArray(apps) ? apps : []);
          // Fetch permissions for each app
          (Array.isArray(apps) ? apps : []).forEach(app => fetchPermissions(app.id));
        })
        .catch(async err => {
          let msg = "Failed to fetch applications";
          if (err.json) { const data = await err.json(); msg = data.error || msg; }
          setAppError(msg);
        })
        .finally(() => setAppLoading(false));
    }
    // eslint-disable-next-line
  }, [tab, token, apiBase]);

  useEffect(() => {
    if (!apiBase) return;
    if (tab === "licenses" && token) {
      fetch(`${apiBase}/applications`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(apps => {
          setApplications(apps);
          (Array.isArray(apps) ? apps : []).forEach(app => fetchPermissions(app.id));
        });
    }
    // eslint-disable-next-line
  }, [tab, token, apiBase]);

  function fetchPermissions(appId) {
    setPermLoading(pl => ({ ...pl, [appId]: true }));
    setPermError(pe => ({ ...pe, [appId]: "" }));
    fetch(`${apiBase}/applications/${appId}/permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(perms => setPermissions(p => ({ ...p, [appId]: perms })))
      .catch(async err => {
        let msg = "Failed to fetch permissions";
        if (err.json) { const data = await err.json(); msg = data.error || msg; }
        setPermError(pe => ({ ...pe, [appId]: msg }));
      })
      .finally(() => setPermLoading(pl => ({ ...pl, [appId]: false })));
  }

  function handleAddApp(e) {
    e.preventDefault();
    if (!newAppName.trim()) return;
    setAppLoading(true);
    setAppError("");
    fetch(`${apiBase}/applications`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newAppName.trim() })
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(() => {
        setNewAppName("");
        // Refresh apps
        return fetch(`${apiBase}/applications`, { headers: { Authorization: `Bearer ${token}` } });
      })
      .then(res => res.json())
      .then(apps => setApplications(apps))
      .catch(async err => {
        let msg = "Failed to add application";
        if (err.json) { const data = await err.json(); msg = data.error || msg; }
        setAppError(msg);
      })
      .finally(() => setAppLoading(false));
  }

  function handleDeleteApp(appId) {
    if (!window.confirm("Delete this application?")) return;
    setAppLoading(true);
    setAppError("");
    fetch(`${apiBase}/applications/${appId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) return Promise.reject(res);
        setApplications(apps => apps.filter(a => a.id !== appId));
        setPermissions(p => { const cp = { ...p }; delete cp[appId]; return cp; });
      })
      .catch(async err => {
        let msg = "Failed to delete application";
        if (err.json) { const data = await err.json(); msg = data.error || msg; }
        setAppError(msg);
      })
      .finally(() => setAppLoading(false));
  }

  function handleAddPermission(appId) {
    const name = (newPermName[appId] || "").trim();
    if (!name) return;
    setPermLoading(pl => ({ ...pl, [appId]: true }));
    setPermError(pe => ({ ...pe, [appId]: "" }));
    fetch(`${apiBase}/applications/${appId}/permissions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name })
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(() => {
        setNewPermName(np => ({ ...np, [appId]: "" }));
        fetchPermissions(appId);
      })
      .catch(async err => {
        let msg = "Failed to add permission";
        if (err.json) { const data = await err.json(); msg = data.error || msg; }
        setPermError(pe => ({ ...pe, [appId]: msg }));
      })
      .finally(() => setPermLoading(pl => ({ ...pl, [appId]: false })));
  }

  function handleDeletePermission(appId, permId) {
    if (!window.confirm("Delete this permission?")) return;
    setPermLoading(pl => ({ ...pl, [appId]: true }));
    setPermError(pe => ({ ...pe, [appId]: "" }));
    fetch(`${apiBase}/permissions/${permId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) return Promise.reject(res);
        fetchPermissions(appId);
      })
      .catch(async err => {
        let msg = "Failed to delete permission";
        if (err.json) { const data = await err.json(); msg = data.error || msg; }
        setPermError(pe => ({ ...pe, [appId]: msg }));
      })
      .finally(() => setPermLoading(pl => ({ ...pl, [appId]: false })));
  }

  // Fetch applications and permissions for licenses tab
  useEffect(() => {
    if (!apiBase) return;
    if (tab !== "licenses" || !token) return;
    fetch(`${apiBase}/applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(apps => {
        setApplications(apps);
        (applications || []).forEach(app => fetchPermissions(app.id));
      });
    // eslint-disable-next-line
  }, [tab, token, apiBase]);

  // Add License button handler
  function openAddLicenseDialog() {
    setLicenseDialogMode("add");
    // Calculate default expiration date: 1 year and 1 day from today at 00:00
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    midnight.setFullYear(midnight.getFullYear() + 1);
    midnight.setDate(midnight.getDate() + 1);
    // Format for datetime-local input: YYYY-MM-DDTHH:mm
    const defaultExpiration = midnight.toISOString().slice(0, 16);
    const defaultAppId = applications.length > 0 ? applications[0].id : "";
    setLicenseForm({ id: null, clientName: "", expirationDate: defaultExpiration, allowExpiredValidation: false });
    setLicenseAppId(defaultAppId);
    // Set all permissions to true by default for the selected app
    const defaultPerms = {};
    (permissions[defaultAppId] || []).forEach(perm => { defaultPerms[perm.id] = true; });
    setLicensePerms(defaultPerms);
    setLicenseDialogOpen(true);
    setLicenseError("");
  }

  // Edit License button handler
  function openEditLicenseDialog(license) {
    setLicenseDialogMode("edit");
    setLicenseForm({
      id: license.id,
      clientName: license.clientName,
      expirationDate: license.expirationDate?.slice?.(0, 16) || "",
      allowExpiredValidation: !!license.allowExpiredValidation,
    });
    setLicenseAppId(license.appId);
    // Map permissions to { permId: bool }
    const perms = {};
    (license.permissions || []).forEach(p => { perms[p.permissionId] = !!p.enabled; });
    setLicensePerms(perms);
    setLicenseDialogOpen(true);
    setLicenseError("");
  }

  function handleLicensePermChange(permId, checked) {
    setLicensePerms(p => ({ ...p, [permId]: checked }));
  }

  function handleLicenseFormChange(field, value) {
    setLicenseForm(f => ({ ...f, [field]: value }));
  }

  function handleLicenseAppChange(appId) {
    setLicenseAppId(appId);
    // Set all permissions to true by default for the new app
    const newPerms = {};
    (permissions[appId] || []).forEach(perm => { newPerms[perm.id] = true; });
    setLicensePerms(newPerms);
  }

  function handleLicenseDialogClose() {
    setLicenseDialogOpen(false);
  }

  async function handleLicenseDialogSave() {
    setLicenseLoading(true);
    setLicenseError("");
    try {
      const permsArr = (permissions[licenseAppId] || []).map(perm => ({
        permissionId: perm.id,
        name: perm.name,
        enabled: !!licensePerms[perm.id]
      }));
      const body = {
        appId: licenseAppId,
        clientName: licenseForm.clientName,
        expirationDate: new Date(licenseForm.expirationDate).toISOString(),
        permissions: permsArr,
        allowExpiredValidation: !!licenseForm.allowExpiredValidation
      };
      let url = `${apiBase}/licenses`;
      let method = "POST";
      if (licenseDialogMode === "edit") {
        url = `${apiBase}/licenses/${licenseForm.id}`;
        method = "PUT";
      }
      const res = await fetchWithAuth(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to save license");
      setLicenseDialogOpen(false);
      fetchLicenses();
    } catch (e) {
      setLicenseError(e.message);
    }
    setLicenseLoading(false);
  }

  async function validateLicense(license) {
    const appName = (applications.find(a => a.id === license.appId) || {}).name || "";
    const hostname = window.location.hostname;
    const version = "1.0.0";
    getLocalIP(async (localIp) => {
      const url = `${apiBase}/validate?licenseKey=${license.licenseKey}&version=${version}&appName=${encodeURIComponent(appName)}&hostname=${encodeURIComponent(hostname)}&localIp=${encodeURIComponent(localIp || "")}`;
      const res = await fetch(url);
      const data = await res.json();
      alert(JSON.stringify(data, null, 2));
    });
  }

  function saveToken(t, role, username) {
    setToken(t);
    setUserRole(role);
    if (username) { setLoginUsername(username); localStorage.setItem("admin_username", username); }
    localStorage.setItem("admin_token", t);
    localStorage.setItem("admin_role", role);
  }

  function clearToken() {
    setToken("");
    setUserRole("");
    setLoginUsername('');
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_username");
  }

  async function fetchWithAuth(url, opts = {}) {
    const res = await fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status === 401) {
      clearToken();
      throw new Error("Session expired. Please log in again.");
    }
    return res;
  }

  async function fetchLicenses() {
    setLoading(true);
    setError("");
    try {
      const queryParams = new URLSearchParams({
        limit: licenseRowsPerPage,
        offset: licensePage * licenseRowsPerPage,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
      });

      if (licenseFilters.licenseId) queryParams.append('licenseId', licenseFilters.licenseId);
      if (licenseFilters.clientName) queryParams.append('clientName', licenseFilters.clientName);
      if (licenseFilters.applicationName) queryParams.append('applicationName', licenseFilters.applicationName);
      if (licenseFilters.expirationStart) queryParams.append('expirationStart', new Date(licenseFilters.expirationStart).toISOString());
      if (licenseFilters.expirationEnd) queryParams.append('expirationEnd', new Date(licenseFilters.expirationEnd).toISOString());

      const res = await fetchWithAuth(`${apiBase}/licenses/paginated?${queryParams.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch licenses");
      const responseData = await res.json();
      setLicenses(responseData.data || []);
      setLicenseTotal(responseData.total || 0);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  async function addLicense(e) {
    e.preventDefault();
    setError("");
    try {
      const res = await fetchWithAuth(`${apiBase}/licenses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appType: form.appType,
          permissions: JSON.parse(form.permissions),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to add license");
      setForm({ appType: "Arkhon", permissions: "{}" });
      fetchLicenses();
    } catch (e) {
      setError(e.message);
    }
  }

  async function deleteLicense(id) {
    if (!window.confirm("Delete this license?")) return;
    setError("");
    try {
      const res = await fetchWithAuth(`${apiBase}/licenses/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to delete");
      fetchLicenses();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    if (!recaptchaToken) {
      setRecaptchaError("Please complete the CAPTCHA");
      return;
    }
    setRecaptchaError("");
    try {
      const res = await fetch(`${apiBase}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...login, recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");
      saveToken(data.token, data.role || "admin", login.username);
    } catch (e) {
      setLoginError(e.message);
    }
  }

  async function handleLogout() {
    try {
      await fetch(`${apiBase}/logout`, { method: "POST" });
    } catch (e) {
      // Ignore errors, just clear token
    }
    clearToken();
  }

  // When app_type changes, set all permissions to true
  function handleAppTypeChange(e) {
    const appType = e.target.value;
    const perms = {};
    setAddForm(f => ({ ...f, appType: appType, permissions: perms }));
  }

  // Add License form submit
  async function handleAddLicense(e) {
    e.preventDefault();
    setAddError("");
    // Validate
    if (!addForm.appType) return setAddError("App type is required");
    if (!addForm.clientName) return setAddError("Client name is required");
    const perms = addForm.permissions;
    if (!expirationDate) return setAddError("Expiration date is required");
    // Submit
    try {
      const res = await fetchWithAuth(`${apiBase}/licenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appType: addForm.appType,
          clientName: addForm.clientName,
          permissions: perms,
          expirationDate: expirationDate.toISOString(),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to add license");
      setAddForm({ appType: "", clientName: "", permissions: {} });
      setExpirationDate(new Date());
      fetchLicenses();
    } catch (e) {
      setAddError(e.message);
    }
  }

  // Open edit dialog
  function handleEditClick(license) {
    setEditLicense(license);
    setEditForm({
      appType: license.appType,
      clientName: license.clientName,
      permissions: license.permissions || {},
    });
    setEditExpirationDate(license.expirationDate ? new Date(license.expirationDate) : new Date());
    setEditError("");
  }

  // Handle edit form changes
  function handleEditFormChange(field, value) {
    setEditForm(f => ({ ...f, [field]: value }));
  }

  function handleEditPermissionChange(key, checked) {
    setEditForm(f => ({
      ...f,
      permissions: { ...f.permissions, [key]: checked },
    }));
  }

  // Submit edit
  async function handleEditSubmit(e) {
    e.preventDefault();
    setEditError("");
    if (!editForm.appType) return setEditError("App type is required");
    if (!editForm.clientName) return setEditError("Client name is required");
    if (!editExpirationDate) return setEditError("Expiration date is required");
    try {
      const res = await fetchWithAuth(`${apiBase}/licenses/${editLicense.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appType: editForm.appType,
          clientName: editForm.clientName,
          permissions: editForm.permissions,
          expirationDate: editExpirationDate.toISOString(),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to update license");
      setEditLicense(null);
      setEditForm(null);
      setEditExpirationDate(new Date());
      fetchLicenses();
    } catch (e) {
      setEditError(e.message);
    }
  }

  function handleEditClose() {
    setEditLicense(null);
    setEditForm(null);
    setEditExpirationDate(new Date());
    setEditError("");
  }

  // Trigger refetch when filters are cleared
  useEffect(() => {
    const isCleared = Object.values(licenseFilters).every(x => x === '' || x === null);
    if (isCleared && apiBase && token) {
      fetchLicenses();
    }
    // eslint-disable-next-line
  }, [licenseFilters]);

  if (!token) {
    return (
      <Container maxWidth="xs">
        <Box mt={10}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              FTech License Manager
            </Typography>
            {loginError && <Alert severity="error">{loginError}</Alert>}
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                value={login.username}
                onChange={e => setLogin({ ...login, username: e.target.value })}
                autoFocus
                required
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={login.password}
                onChange={e => setLogin({ ...login, password: e.target.value })}
                required
              />
              {recaptchaSiteKey ? (
                <ReCAPTCHA
                  sitekey={recaptchaSiteKey}
                  onChange={token => { setRecaptchaToken(token); setRecaptchaError(""); }}
                  onExpired={() => setRecaptchaToken("")}
                />
              ) : (
                <p>reCAPTCHA yükleniyor...</p>
              )}
              {recaptchaError && <Alert severity="error" sx={{ mt: 1 }}>{recaptchaError}</Alert>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
              >
                Log In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Licenses are now sorted server-side
  const sortedLicenses = Array.isArray(licenses) ? licenses : [];

  // License filter handlers
  const handleLicenseFilterChange = (e) => {
    setLicenseFilters({
      ...licenseFilters,
      [e.target.name]: e.target.value,
    });
  };

  const handleLicenseSearch = () => {
    setLicensePage(0);
    fetchLicenses();
  };

  const handleLicenseClearFilters = () => {
    setLicenseFilters({
      licenseId: '',
      clientName: '',
      applicationName: '',
      expirationStart: null,
      expirationEnd: null,
    });
    setLicensePage(0);
  };

  const handleLicenseChangePage = (event, newPage) => {
    setLicensePage(newPage);
  };

  const handleLicenseChangeRowsPerPage = (event) => {
    setLicenseRowsPerPage(parseInt(event.target.value, 10));
    setLicensePage(0);
  };

  function handleStatsLoaded(stats) {
    if (stats.latestVersion) setLatestVersion(stats.latestVersion);
    if (stats.alerts) setDashAlerts(stats.alerts);
  }

  function handleNavigate(tabName, params) {
    setTab(tabName);
    if (tabName === 'licenses' && params?.licenseId) {
      setLicenseFilters(f => ({ ...f, licenseId: String(params.licenseId) }));
    }
  }

  const notifItems = dashAlerts ? [
    ...(dashAlerts.frequentDay || []).slice(0, 3).map(a => ({
      type: 'freq',
      title: `${a.clientName || `#${a.licenseId}`} validated ${a.count}× today`,
      sub: `${a.appName} · High-frequency alert`,
    })),
    ...(dashAlerts.expiringSoon || []).slice(0, 3).map(a => ({
      type: 'expiring',
      title: `${a.clientName || `#${a.licenseId}`} expires in ${a.daysLeft}d`,
      sub: `${a.appName} · Expiring soon`,
    })),
  ] : [];
  const notifCount = notifItems.length;

  const initials = loginUsername
    ? loginUsername.slice(0, 2).toUpperCase()
    : (userRole ? userRole.slice(0, 2).toUpperCase() : '??');

  const tabDef = [
    { value: 'dashboard', label: 'Dashboard', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg> },
    { value: 'history', label: 'History', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/><path d="M12 7v5l3 2"/></svg> },
    { value: 'licenses', label: 'Licenses', badge: licenseTotal, icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><circle cx="8" cy="15" r="1.2"/></svg> },
    ...(userRole === 'admin' ? [{ value: 'applications', label: 'Applications', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="8" rx="1.5"/><rect x="3" y="13" width="8" height="8" rx="1.5"/><rect x="13" y="13" width="8" height="8" rx="1.5"/></svg> }] : []),
    { value: 'versions', label: 'Versions', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20"/><path d="m5 9 7-7 7 7"/><path d="M5 15h14"/></svg> },
    ...(userRole === 'admin' ? [{ value: 'global-ip-rules', label: 'Global IP Rules', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> }] : []),
  ];

  return (
    <Box sx={{ background: '#FAFAF9', minHeight: '100vh', fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* ── topbar ── */}
      <Box component="header" sx={{ background: '#0B1020', borderBottom: '1px solid #0B1020' }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: '28px', height: 52, display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em', color: '#E2E8F0' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '6px', background: 'linear-gradient(135deg,#6366F1,#4338CA)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 700, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.1)' }}>L</Box>
            License Manager
            <Box component="span" sx={{ color: '#64748B', fontWeight: 400, fontSize: 12, ml: '2px' }}>/ Production</Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', px: '10px', py: '6px', borderRadius: '7px', width: 280, color: '#94A3B8', fontSize: 12, '&:focus-within': { background: 'rgba(255,255,255,.08)', borderColor: 'rgba(129,140,248,.4)' } }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
            <Box component="input" placeholder="Search clients, licenses, IPs…" sx={{ flex: 1, background: 'none', border: 0, outline: 0, color: '#E2E8F0', fontSize: 12, fontFamily: 'inherit', '&::placeholder': { color: '#64748B' } }} />
            <Box component="span" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, px: '5px', py: '1px', border: '1px solid rgba(255,255,255,.1)', borderRadius: '4px', color: '#64748B' }}>⌘K</Box>
          </Box>

          {/* icon buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Refresh */}
            <Box component="button" title="Refresh" sx={{ width: 30, height: 30, borderRadius: '7px', color: '#94A3B8', display: 'grid', placeItems: 'center', border: 0, background: 'none', cursor: 'pointer', position: 'relative', transition: '.15s', '&:hover': { background: 'rgba(255,255,255,.06)', color: '#E2E8F0' } }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
            </Box>
            {/* Notifications */}
            <Box sx={{ position: 'relative' }}>
              <Box component="button" title="Notifications" onClick={() => setNotifOpen(o => !o)} sx={{ width: 30, height: 30, borderRadius: '7px', color: notifOpen ? '#E2E8F0' : '#94A3B8', display: 'grid', placeItems: 'center', border: 0, background: notifOpen ? 'rgba(255,255,255,.06)' : 'none', cursor: 'pointer', position: 'relative', transition: '.15s', '&:hover': { background: 'rgba(255,255,255,.06)', color: '#E2E8F0' } }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/></svg>
                {notifCount > 0 && <Box sx={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: '50%', background: '#F43F5E', boxShadow: '0 0 0 2px #0B1020' }} />}
              </Box>
              {notifOpen && (
                <Box sx={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 300, background: '#fff', border: '1px solid #E7E5E4', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,23,42,.12)', zIndex: 200, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: '10px', borderBottom: '1px solid #E7E5E4', fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 600, fontSize: 13, color: '#0B1020', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Notifications
                    {notifCount > 0 && <Box component="span" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, px: '6px', py: '1px', borderRadius: 999, background: '#FFF1F2', color: '#E11D48', fontWeight: 600 }}>{notifCount}</Box>}
                    <Box component="button" onClick={() => setNotifOpen(false)} sx={{ border: 0, background: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 18, lineHeight: 1, p: '2px 4px', borderRadius: 4, ml: 'auto' }}>×</Box>
                  </Box>
                  {notifItems.length === 0 ? (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center', fontSize: 12, color: '#94A3B8' }}>No alerts</Box>
                  ) : (
                    <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                      {notifItems.map((item, i) => (
                        <Box key={i} sx={{ px: 2, py: '10px', borderBottom: '1px solid #F6F6F4', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: item.type === 'expiring' ? '#D97706' : '#E11D48', mt: '5px', flexShrink: 0 }} />
                          <Box>
                            <Box sx={{ fontSize: 12, fontWeight: 500, color: '#1F2937', lineHeight: 1.3 }}>{item.title}</Box>
                            <Box sx={{ fontSize: 11, color: '#94A3B8', mt: '2px' }}>{item.sub}</Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {/* Settings */}
            <Box component="button" title="Settings" sx={{ width: 30, height: 30, borderRadius: '7px', color: '#94A3B8', display: 'grid', placeItems: 'center', border: 0, background: 'none', cursor: 'pointer', position: 'relative', transition: '.15s', '&:hover': { background: 'rgba(255,255,255,.06)', color: '#E2E8F0' } }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>
            </Box>
            {/* user widget */}
            <Box sx={{ position: 'relative', ml: '6px' }}>
              <Box component="button" onClick={() => setUserMenuOpen(o => !o)} sx={{
                display: 'inline-flex', alignItems: 'center', gap: '8px', height: 34, px: '8px', pr: '10px',
                borderRadius: '8px', border: userMenuOpen ? '1px solid rgba(129,140,248,.4)' : '1px solid rgba(255,255,255,.08)',
                background: userMenuOpen ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.04)',
                cursor: 'pointer', transition: '.15s', '&:hover': { background: 'rgba(255,255,255,.08)', borderColor: 'rgba(255,255,255,.12)' },
              }}>
                <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg,#818CF8,#4F46E5)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  {initials}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                  <Box sx={{ fontSize: 12, fontWeight: 500, color: '#E2E8F0', fontFamily: "'Inter',system-ui" }}>{loginUsername || 'User'}</Box>
                  <Box sx={{ fontSize: 10, color: '#64748B', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '.04em' }}>{userRole}</Box>
                </Box>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" style={{ marginLeft: 2, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: '.15s' }}><path d="m6 9 6 6 6-6"/></svg>
              </Box>
              {userMenuOpen && (
                <Box sx={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 200, background: '#fff', border: '1px solid #E7E5E4', borderRadius: '10px', boxShadow: '0 8px 24px rgba(15,23,42,.12)', zIndex: 200, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: '12px', borderBottom: '1px solid #F6F6F4' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#818CF8,#4F46E5)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {initials}
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: 13, fontWeight: 600, color: '#0B1020', fontFamily: "'Inter Tight','Inter',sans-serif" }}>{loginUsername || 'User'}</Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: '2px', px: '6px', py: '1px', borderRadius: 999, fontSize: 10, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '.04em', background: userRole === 'admin' ? '#EEF2FF' : '#F6F6F4', color: userRole === 'admin' ? '#4F46E5' : '#64748B', border: userRole === 'admin' ? '1px solid #C7D2FE' : '1px solid #E7E5E4' }}>
                          {userRole}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box component="button" onClick={handleLogout} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: 2, py: '10px', border: 0, background: 'none', cursor: 'pointer', color: '#E11D48', fontSize: 12, fontFamily: "'Inter',system-ui", fontWeight: 500, transition: '.12s', '&:hover': { background: '#FFF1F2' } }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="m10 17 5-5-5-5"/><path d="M15 12H3"/></svg>
                    Sign out
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {userRole === 'readonly' && (
        <Box sx={{ p: 1.5, textAlign: 'center', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 500, borderBottom: '1px solid #fde68a' }}>
          READONLY MODE — View only. Add / Edit / Delete are disabled.
        </Box>
      )}

      {/* ── tab navigation ── */}
      <Box component="nav" sx={{ background: '#fff', borderBottom: '1px solid #E7E5E4', position: 'sticky', top: 0, zIndex: 20 }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: '20px', display: 'flex', alignItems: 'center', height: 44 }}>
          {tabDef.map(t => (
            <Box
              key={t.value}
              component="button"
              onClick={() => setTab(t.value)}
              sx={{
                px: '14px', height: 44, display: 'flex', alignItems: 'center', gap: 1,
                color: tab === t.value ? '#4F46E5' : '#64748B',
                fontSize: 12.5, fontWeight: 500, letterSpacing: '.01em', border: 0,
                background: 'none', cursor: 'pointer', fontFamily: 'inherit',
                borderBottom: tab === t.value ? '2px solid #4F46E5' : '2px solid transparent',
                transition: '.12s',
                '&:hover': { color: tab === t.value ? '#4F46E5' : '#1F2937' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>{t.icon}</Box>
              {t.label}
              {t.badge > 0 && (
                <Box component="span" sx={{
                  background: tab === t.value ? '#EEF2FF' : '#F6F6F4',
                  color: tab === t.value ? '#4F46E5' : '#64748B',
                  fontSize: 10.5, fontWeight: 600, px: '6px', py: '1px', borderRadius: 999,
                  fontFamily: "'JetBrains Mono',monospace",
                }}>{t.badge}</Box>
              )}
            </Box>
          ))}

          {/* right side chips */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '9px', py: '4px', borderRadius: '6px', background: '#F6F6F4', border: '1px solid #E7E5E4', fontSize: 11.5, color: '#475569', fontWeight: 500 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 0 3px rgba(16,185,129,.15)', animation: 'lm-pulse 2s ease-in-out infinite' }} />
              All systems operational
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '9px', py: '4px', borderRadius: '6px', background: '#F6F6F4', border: '1px solid #E7E5E4', fontSize: 10.5, color: '#475569', fontFamily: "'JetBrains Mono',monospace" }}>
              {latestVersion ? (latestVersion.startsWith('v') ? latestVersion : `v${latestVersion}`) : 'v—'}
            </Box>
          </Box>
        </Box>
      </Box>
      <style>{`@keyframes lm-pulse{50%{box-shadow:0 0 0 5px rgba(16,185,129,.05)}}`}</style>
      {/* Dashboard tab content */}
      {tab === "dashboard" && (
        <DashboardTab apiBase={apiBase} token={token} onNavigate={handleNavigate} onNewLicense={openAddLicenseDialog} onStatsLoaded={handleStatsLoaded} />
      )}
      {/* History tab content */}
      {tab === "history" && (
        <HistoryTab apiBase={apiBase} token={token} userRole={userRole} />
      )}
      {/* Only show Licenses UI if tab is 'licenses' */}
      {tab === "licenses" && (() => {
        const LTH = ({ children, sortKey }) => (
          <th onClick={sortKey ? () => handleSort(sortKey) : undefined} style={{
            textAlign: 'left', fontSize: 10.5, color: '#64748B', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '.05em', padding: '9px 12px',
            borderBottom: '1px solid #E7E5E4', background: '#FCFCFB',
            fontFamily: "'Inter',system-ui,sans-serif", whiteSpace: 'nowrap',
            cursor: sortKey ? 'pointer' : 'default',
          }}>
            {children}
            {sortKey && sortColumn === sortKey && <span style={{ marginLeft: 4 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
          </th>
        );
        const isExp = (d) => d && new Date(d) < new Date();
        const isExpSoon = (d) => { if (!d) return false; const diff = (new Date(d) - Date.now()) / 86400000; return diff > 0 && diff <= 30; };
        return (
          <div style={{ maxWidth: 1440, margin: '0 auto', padding: '18px 20px 60px', background: '#FAFAF9', minHeight: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11.5, color: '#94A3B8', marginBottom: 3 }}>
                  License Manager &nbsp;/&nbsp; <b style={{ color: '#64748B', fontWeight: 500 }}>Licenses</b>
                </div>
                <h1 style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: '#0B1020' }}>Licenses</h1>
              </div>
              {userRole === "admin" && (
                <button onClick={openAddLicenseDialog} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px',
                  borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: '1px solid #4F46E5', background: '#4F46E5', color: '#fff',
                  fontFamily: "'Inter',system-ui,sans-serif",
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  Add License
                </button>
              )}
            </div>
            {/* filter bar */}
            <div style={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 10, padding: '12px 16px', boxShadow: '0 1px 2px rgba(15,23,42,.04)', marginBottom: 12 }}>
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                <TextField label="License ID" name="licenseId" value={licenseFilters.licenseId} onChange={handleLicenseFilterChange} size="small" sx={{ width: 120 }} />
                <TextField label="Client Name" name="clientName" value={licenseFilters.clientName} onChange={handleLicenseFilterChange} size="small" sx={{ width: 180 }} />
                <Autocomplete
                  options={applications} getOptionLabel={(option) => option.name || ''}
                  value={applications.find(a => a.name === licenseFilters.applicationName) || null}
                  onChange={(_, newValue) => setLicenseFilters({ ...licenseFilters, applicationName: newValue ? newValue.name : '' })}
                  renderInput={(params) => <TextField {...params} label="Application" size="small" />}
                  sx={{ width: 200 }} isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker label="Expiration From" value={licenseFilters.expirationStart} onChange={(val) => setLicenseFilters({ ...licenseFilters, expirationStart: val })} slotProps={{ textField: { size: 'small', sx: { width: 200 } }, field: { clearable: true } }} />
                  <DateTimePicker label="Expiration To" value={licenseFilters.expirationEnd} onChange={(val) => setLicenseFilters({ ...licenseFilters, expirationEnd: val })} slotProps={{ textField: { size: 'small', sx: { width: 200 } }, field: { clearable: true } }} />
                </LocalizationProvider>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <button onClick={handleLicenseSearch} style={{ height: 36, padding: '0 16px', borderRadius: 7, border: '1px solid #4F46E5', background: '#4F46E5', color: '#fff', cursor: 'pointer', fontSize: 12, fontFamily: "'Inter',system-ui,sans-serif", fontWeight: 500 }}>Search</button>
                  <button onClick={handleLicenseClearFilters} style={{ height: 36, padding: '0 14px', borderRadius: 7, border: '1px solid #E7E5E4', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 12, fontFamily: "'Inter',system-ui,sans-serif", fontWeight: 500 }}>Clear</button>
                </Box>
              </Box>
            </div>
            {/* table */}
            <div style={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 10, boxShadow: '0 1px 2px rgba(15,23,42,.04)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <LTH sortKey="id">ID</LTH>
                      <LTH>Client Name</LTH>
                      <LTH>Application</LTH>
                      <LTH>Permissions</LTH>
                      <LTH sortKey="expirationDate">Expiration</LTH>
                      <LTH>Actions</LTH>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px 0' }}>
                        <CircularProgress size={24} />
                      </td></tr>
                    ) : sortedLicenses.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: '#94A3B8', fontSize: 12 }}>No licenses found.</td></tr>
                    ) : sortedLicenses.map(license => {
                      const expired = isExp(license.expirationDate);
                      const soon = !expired && isExpSoon(license.expirationDate);
                      return (
                        <tr key={license.id} style={{ cursor: 'default' }}>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={license.licenseKey} arrow>
                                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#475569', cursor: 'pointer' }}>#{String(license.id).padStart(4,'0')}</span>
                              </Tooltip>
                              <Tooltip title={license.licenseKey} arrow>
                                <IconButton size="small" sx={{ p: 0.25, color: '#94A3B8' }}><InfoOutlinedIcon sx={{ fontSize: 14 }} /></IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA', fontWeight: 500, color: '#0B1020' }}>{license.clientName}</td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 500, background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', fontFamily: "'Inter',system-ui" }}>
                              {(applications.find(a => a.id === license.appId) || {}).name || '—'}
                            </span>
                          </td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA', fontSize: 11.5, color: '#475569' }}>
                            {(license.permissions || []).filter(p => p.enabled).map(p => p.name).join(', ') || '—'}
                          </td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: expired ? '#E11D48' : soon ? '#D97706' : '#475569', fontWeight: expired || soon ? 600 : 400 }}>
                            {license.expirationDate ? format(new Date(license.expirationDate), 'dd/MM/yyyy HH:mm') : '—'}
                            {expired && <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: '#FFF1F2', color: '#E11D48', border: '1px solid #FECDD3', fontFamily: "'Inter'" }}>Expired</span>}
                            {soon && <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A', fontFamily: "'Inter'" }}>Soon</span>}
                          </td>
                          <td style={{ padding: '9px 12px', borderBottom: '1px solid #EFEDEA' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {userRole !== 'readonly' && (
                                <button onClick={() => openEditLicenseDialog(license)} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid #E7E5E4', background: '#fff', color: '#1F2937', cursor: 'pointer', fontSize: 11.5, fontFamily: "'Inter'" }}>Edit</button>
                              )}
                              {userRole !== 'readonly' && (
                                <button onClick={() => deleteLicense(license.id)} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid #FECDD3', background: '#FFF1F2', color: '#E11D48', cursor: 'pointer', fontSize: 11.5, fontFamily: "'Inter'" }}>Delete</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ borderTop: '1px solid #E7E5E4' }}>
                <TablePagination component="div" count={licenseTotal} page={licensePage} onPageChange={handleLicenseChangePage} rowsPerPage={licenseRowsPerPage} onRowsPerPageChange={handleLicenseChangeRowsPerPage} rowsPerPageOptions={[10, 20, 50, 100]} sx={{ color: '#475569', fontSize: 12 }} />
              </div>
            </div>
          </div>
        );
      })()}
      {/* Applications tab content will be implemented in the next step */}
      {tab === "applications" && userRole === "admin" && (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '18px 20px 60px', background: '#FAFAF9', minHeight: '100%' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, color: '#94A3B8', marginBottom: 3 }}>
              License Manager &nbsp;/&nbsp; <b style={{ color: '#64748B', fontWeight: 500 }}>Applications</b>
            </div>
            <h1 style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: '#0B1020' }}>Applications</h1>
          </div>
          {/* add app */}
          <div style={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 2px rgba(15,23,42,.04)', marginBottom: 12 }}>
            <form onSubmit={handleAddApp} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <TextField label="New Application Name" value={newAppName} onChange={e => setNewAppName(e.target.value)} size="small" sx={{ width: 280 }} />
              <button type="submit" disabled={appLoading || !newAppName.trim()} style={{
                height: 40, padding: '0 16px', borderRadius: 7, border: '1px solid #4F46E5', background: '#4F46E5',
                color: '#fff', cursor: appLoading || !newAppName.trim() ? 'not-allowed' : 'pointer',
                fontSize: 12, fontFamily: "'Inter',system-ui", fontWeight: 500, opacity: appLoading || !newAppName.trim() ? .5 : 1,
              }}>Add Application</button>
            </form>
          </div>
          {appError && <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', color: '#E11D48', fontSize: 12, marginBottom: 10 }}>{appError}</div>}
          {appLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}><CircularProgress /></div>
          ) : applications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94A3B8', fontSize: 12 }}>No applications found.</div>
          ) : (
            applications.map(app => (
              <div key={app.id} style={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 10, padding: '16px', boxShadow: '0 1px 2px rgba(15,23,42,.04)', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontFamily: "'Inter Tight','Inter',sans-serif", fontWeight: 600, fontSize: 14, color: '#0B1020' }}>{app.name}</div>
                  <Tooltip title={app.inUse ? "Cannot delete: in use by a license" : "Delete App"}>
                    <span>
                      <button onClick={() => handleDeleteApp(app.id)} disabled={app.inUse} style={{
                        height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid #FECDD3',
                        background: app.inUse ? '#F6F6F4' : '#FFF1F2', color: app.inUse ? '#94A3B8' : '#E11D48',
                        cursor: app.inUse ? 'not-allowed' : 'pointer', fontSize: 11.5, fontFamily: "'Inter'", display: 'inline-flex', alignItems: 'center', gap: 5,
                      }}>
                        {app.inUse && <LockIcon sx={{ fontSize: 12 }} />}
                        Delete App
                      </button>
                    </span>
                  </Tooltip>
                </div>
                <div style={{ borderTop: '1px solid #EFEDEA', paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Permissions</div>
                  <form onSubmit={e => { e.preventDefault(); handleAddPermission(app.id); }} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <TextField label="New Permission Name" value={newPermName[app.id] || ""} onChange={e => setNewPermName(np => ({ ...np, [app.id]: e.target.value }))} size="small" sx={{ width: 240 }} />
                    <button type="submit" disabled={permLoading[app.id] || !(newPermName[app.id] || "").trim()} style={{
                      height: 40, padding: '0 14px', borderRadius: 7, border: '1px solid #E7E5E4', background: '#fff',
                      color: '#1F2937', cursor: 'pointer', fontSize: 12, fontFamily: "'Inter'",
                    }}>Add</button>
                  </form>
                  {permError[app.id] && <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 6, padding: '8px 12px', color: '#E11D48', fontSize: 12, marginBottom: 8 }}>{permError[app.id]}</div>}
                  {permLoading[app.id] ? <CircularProgress size={18} /> : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(!permissions[app.id] || permissions[app.id].length === 0) ? (
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>No permissions.</span>
                      ) : (
                        permissions[app.id].map(perm => (
                          <div key={perm.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 999, border: '1px solid #E7E5E4', background: '#F6F6F4', fontSize: 12, color: '#1F2937' }}>
                            {perm.name}
                            <Tooltip title={perm.inUse ? "Cannot delete: in use by a license" : "Delete Permission"}>
                              <span>
                                <button onClick={() => handleDeletePermission(app.id, perm.id)} disabled={perm.inUse} style={{
                                  width: 18, height: 18, borderRadius: '50%', border: 0,
                                  background: perm.inUse ? 'none' : '#FECDD3', color: perm.inUse ? '#94A3B8' : '#E11D48',
                                  cursor: perm.inUse ? 'not-allowed' : 'pointer', fontSize: 12, lineHeight: 1, display: 'grid', placeItems: 'center',
                                }}>×</button>
                              </span>
                            </Tooltip>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* Global IP Rules tab content */}
      {tab === "global-ip-rules" && userRole === "admin" && (
        <GlobalIpRules apiBase={apiBase} token={token} />
      )}
      {/* Versions tab content */}
      {tab === "versions" && (
        <VersionsTab apiBase={apiBase} token={token} userRole={userRole} />
      )}
      {/* Edit License Dialog */}
      <Dialog open={!!editLicense} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit License</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          {editForm && (
            <form onSubmit={handleEditSubmit} id="edit-license-form">
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="Client Name"
                  value={editForm.clientName}
                  onChange={e => handleEditFormChange("clientName", e.target.value)}
                  required
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel id="edit-app-type-label">App Type</InputLabel>
                  <Select
                    labelId="edit-app-type-label"
                    id="edit-app-type-select"
                    value={editForm.appType}
                    label="App Type"
                    onChange={e => handleEditFormChange("appType", e.target.value)}
                  >
                    <MenuItem value=""><em>Select...</em></MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Expiration Date"
                  value={editExpirationDate}
                  onChange={setEditExpirationDate}
                  inputFormat="dd/MM/yyyy HH:mm"
                  renderInput={(params) => <TextField {...params} required fullWidth margin="normal" />}
                />
              </LocalizationProvider>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button type="submit" form="edit-license-form" variant="contained" color="primary" disabled={userRole === 'readonly'}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* License Add/Edit Dialog */}
      <Dialog open={licenseDialogOpen} onClose={handleLicenseDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{licenseDialogMode === "add" ? "Add License" : "Edit License"}</DialogTitle>
        <DialogContent>
          {licenseError && <Alert severity="error" sx={{ mb: 2 }}>{licenseError}</Alert>}
          <TextField label="Client Name" value={licenseForm.clientName} onChange={e => handleLicenseFormChange("clientName", e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Expiration Date" type="datetime-local" value={licenseForm.expirationDate} onChange={e => handleLicenseFormChange("expirationDate", e.target.value)} fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="app-select-label">Application</InputLabel>
            <Select labelId="app-select-label" value={licenseAppId} label="Application" onChange={e => handleLicenseAppChange(e.target.value)} disabled={licenseDialogMode === "edit"}>
              {applications.map(app => (
                <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(permissions[licenseAppId] || []).length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              No permissions defined for this application.
            </Typography>
          ) : (
            (permissions[licenseAppId] || []).map(perm => (
              <FormControlLabel
                key={perm.id}
                control={<Checkbox checked={!!licensePerms[perm.id]} onChange={e => handleLicensePermChange(perm.id, e.target.checked)} />}
                label={perm.name}
              />
            ))
          )}
          <FormControlLabel
            control={<Checkbox checked={!!licenseForm.allowExpiredValidation} onChange={e => setLicenseForm(f => ({ ...f, allowExpiredValidation: e.target.checked }))} />}
            label="Allow validation even if expired (returns warning)"
            sx={{ mt: 2, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLicenseDialogClose}>Cancel</Button>
          {userRole === "admin" && (
            <Button onClick={handleLicenseDialogSave} variant="contained" disabled={licenseLoading}>{licenseDialogMode === "add" ? "Add" : "Save"}</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
